let activeMagnify = null;
const magnifyPreloads = new WeakMap();

function clampMagnify(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function getMagnifySource(image) {
  return image.currentSrc || image.src;
}

function getLargestSrcsetSource(image) {
  const srcset = image.getAttribute('srcset');
  if (!srcset) return getMagnifySource(image);
  let best = { url: getMagnifySource(image), width: 0 };
  srcset.split(',').forEach((candidate) => {
    const bits = candidate.trim().split(/\s+/);
    const url = bits[0];
    const width = bits[1] && /w$/.test(bits[1]) ? parseInt(bits[1], 10) : 0;
    if (url && width >= best.width) best = { url, width };
  });
  return best.url || getMagnifySource(image);
}

function preloadMagnifySource(image) {
  const src = getLargestSrcsetSource(image);
  const existing = magnifyPreloads.get(image);
  if (existing && existing.src === src) return existing.promise;

  const promise = new Promise((resolve) => {
    if (!src) return resolve(src);
    const preload = new Image();
    preload.src = src;
    const finish = () => {
      if (typeof preload.decode === 'function') {
        preload.decode().catch(() => {}).finally(() => resolve(src));
      } else resolve(src);
    };
    if (preload.complete && preload.naturalWidth > 0) finish();
    else {
      preload.addEventListener('load', finish, { once: true });
      preload.addEventListener('error', () => resolve(src), { once: true });
    }
  });
  magnifyPreloads.set(image, { src, promise });
  return promise;
}

function closeMagnify() {
  if (!activeMagnify) return;
  const { overlay, image } = activeMagnify;
  if (overlay && overlay.isConnected) overlay.remove();
  if (image) image.style.opacity = '';
  activeMagnify = null;
}

function createOverlay(image, src) {
  const overlay = document.createElement('div');
  overlay.className = 'image-magnify-full-size';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.backgroundImage = `url("${String(src).replace(/"/g, '%22')}")`;
  overlay.style.backgroundRepeat = 'no-repeat';
  overlay.style.cursor = 'zoom-out';
  overlay.style.position = 'absolute';
  overlay.style.inset = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.zIndex = '3';
  image.parentElement.style.position = image.parentElement.style.position || 'relative';
  image.parentElement.insertBefore(overlay, image);
  return overlay;
}

function moveMagnify(image, overlay, event, zoomRatio) {
  const rect = overlay.getBoundingClientRect();
  const cx = clampMagnify(event.clientX, rect.left, rect.right);
  const cy = clampMagnify(event.clientY, rect.top, rect.bottom);
  const xPercent = ((cx - rect.left) / rect.width) * 100;
  const yPercent = ((cy - rect.top) / rect.height) * 100;
  overlay.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
  overlay.style.backgroundSize = `${image.clientWidth * zoomRatio}px auto`;
}

function openMagnify(image, event, zoomRatio) {
  closeMagnify();

  // Use the already-rendered responsive source immediately. It is already
  // decoded on screen, so the first zoom appears with no waiting/white flash.
  const visibleSrc = getMagnifySource(image);
  if (!image.isConnected || !visibleSrc) return;

  const overlay = createOverlay(image, visibleSrc);
  activeMagnify = { overlay, image, zoomRatio };
  moveMagnify(image, overlay, event, zoomRatio);

  // Hide the underlying image only after the overlay is in place. This is
  // important for transparent PNG/WebP product shots: the original image can
  // no longer show through the transparent pixels and create a double image.
  requestAnimationFrame(() => {
    if (activeMagnify && activeMagnify.overlay === overlay) image.style.opacity = '0';
  });

  // Upgrade silently to the largest srcset source once it has decoded. The
  // customer continues seeing the current zoom the whole time.
  preloadMagnifySource(image).then((hires) => {
    if (!hires || !activeMagnify || activeMagnify.overlay !== overlay) return;
    overlay.style.backgroundImage = `url("${String(hires).replace(/"/g, '%22')}")`;
  });

  overlay.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeMagnify();
  });
}

function enableZoomOnHover(zoomRatio) {
  // Release 3.5.242 — desktop-only. Mobile uses the separate full-screen
  // inspection viewer and must not inherit any desktop click behavior.
  if (!window.matchMedia('(min-width: 750px)').matches) return;

  document.querySelectorAll('.image-magnify-hover').forEach((image) => {
    if (image.dataset.etMagnifyReady === 'true') return;
    image.dataset.etMagnifyReady = 'true';
    image.style.cursor = 'zoom-in';
    const opener = image.closest('.product__modal-opener--image') || image;
    opener.style.cursor = 'zoom-in';

    // Warm the larger source before the customer clicks when possible.
    opener.addEventListener('pointerenter', () => preloadMagnifySource(image), { passive: true });

    /* The modal-opener's transparent button covers the image in Dawn. Listen
       on its parent in capture phase so the desktop zoom receives the click
       before the hidden modal button can consume it. */
    opener.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (activeMagnify && activeMagnify.image === image) closeMagnify();
      else openMagnify(image, event, zoomRatio);
    }, true);
  });

  if (!document.documentElement.dataset.etMagnifyMoveBound) {
    document.documentElement.dataset.etMagnifyMoveBound = 'true';
    document.addEventListener('mousemove', (event) => {
      if (!activeMagnify) return;
      moveMagnify(activeMagnify.image, activeMagnify.overlay, event, activeMagnify.zoomRatio);
    }, { passive: true });
  }
}

enableZoomOnHover(2);
